import { AiBubble } from "./AiBubble";

/**
 * LoadingSequence — the single loading affordance for async intake work.
 *
 * Design intent (sandbox prototype, ready for engineering to lift into prod):
 *
 * 1. One component, several form factors (`variant`):
 *    - "inline"  → a single row, e.g. inside a results table/list while it loads.
 *    - "chat"    → an AI-side chat bubble, for replies the model is still composing.
 *    - "panel"   → a larger centered block for a whole-flow load (product/vendor search).
 *
 * 2. Two types of load, driven by `estimate.kind`:
 *    - "quick"    → short, in-flow work (seconds). Spinner + label + "~Ns".
 *    - "extended" → long async jobs (minutes). Adds the reassurance line so the
 *                   user knows they can walk away without losing the result.
 *
 * 3. A radial spinner always animates, to reassure the user that work is
 *    genuinely still happening (not a frozen screen).
 *
 * 4. REQUIRED time frame. `estimate` has no default on purpose: whenever a
 *    feature uses this component, the engineer MUST declare how long that
 *    specific operation is expected to take in dev/prod. Some jobs run for
 *    minutes; the estimate is what sets the user's expectation, so it must be
 *    a real, per-operation number — not a placeholder.
 */

export type LoadingEstimate =
  | { kind: "quick"; seconds: number }
  | { kind: "extended"; minMinutes: number; maxMinutes: number };

export interface LoadingSequenceProps {
  /**
   * REQUIRED. The real expected duration of *this* operation. Engineers must
   * supply it per loading site — it drives the visible time frame and, for
   * "extended" work, the "you can leave" reassurance.
   */
  estimate: LoadingEstimate;
  /** Verb phrase for the work in progress, e.g. "Searching the vendor library". */
  label: string;
  /** Form factor. Defaults to "panel". */
  variant?: "inline" | "chat" | "panel";
}

/**
 * Operations at or below this many seconds don't display a time estimate — the
 * spinner alone is reassurance enough, and a "~5s" countdown just adds noise.
 * The estimate is still required from engineers (for telemetry / expectations);
 * we simply don't surface it to the user when the work is this short.
 */
const ESTIMATE_MIN_SECONDS = 5;

function shouldShowEstimate(estimate: LoadingEstimate): boolean {
  return (
    estimate.kind === "extended" || estimate.seconds > ESTIMATE_MIN_SECONDS
  );
}

function estimateText(estimate: LoadingEstimate): string {
  if (estimate.kind === "quick") return `~${estimate.seconds}s`;
  if (estimate.minMinutes === estimate.maxMinutes) {
    return `~${estimate.maxMinutes} min`;
  }
  return `${estimate.minMinutes}–${estimate.maxMinutes} min`;
}

const REASSURANCE =
  "You can leave this tab or come back later — we'll save your report.";

function Spinner({ size = 18 }: { size?: number }) {
  return (
    <svg
      className="shrink-0 animate-spin text-(--fg-brand)"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeWidth="2.5"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function EstimatePill({ estimate }: { estimate: LoadingEstimate }) {
  return (
    <span className="inline-flex shrink-0 items-center rounded-full bg-(--bg-sky) px-2 py-0.5 font-[family-name:var(--font-body)] text-[11px] font-semibold text-(--fg-brand)">
      {estimateText(estimate)}
    </span>
  );
}

export function LoadingSequence({
  estimate,
  label,
  variant = "panel",
}: LoadingSequenceProps) {
  const extended = estimate.kind === "extended";
  const showEstimate = shouldShowEstimate(estimate);

  if (variant === "inline") {
    return (
      <div
        role="status"
        className="flex items-center gap-2.5 rounded-[6px] border border-(--border-subtle) bg-(--bg-white) px-[13px] py-[11px] font-[family-name:var(--font-body)]"
      >
        <Spinner size={16} />
        <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-(--fg-primary)">
          {label}
        </span>
        {showEstimate && <EstimatePill estimate={estimate} />}
      </div>
    );
  }

  if (variant === "chat") {
    return (
      <AiBubble>
        <div role="status" className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2.5">
            <Spinner size={16} />
            <span className="text-[14px] font-medium text-(--fg-primary)">
              {label}
            </span>
            {showEstimate && <EstimatePill estimate={estimate} />}
          </div>
          {extended && (
            <span className="text-[12.5px] leading-[1.5] text-(--fg-muted)">
              {REASSURANCE}
            </span>
          )}
        </div>
      </AiBubble>
    );
  }

  // panel
  return (
    <div
      role="status"
      className="flex flex-col items-center gap-3 rounded-[10px] border border-(--border-subtle) bg-(--bg-white) px-6 py-8 text-center font-[family-name:var(--font-body)]"
    >
      <Spinner size={26} />
      <div className="flex flex-col items-center gap-1.5">
        <div className="flex items-center gap-2.5">
          <span className="text-[15px] font-semibold text-(--fg-primary)">
            {label}
          </span>
          {showEstimate && <EstimatePill estimate={estimate} />}
        </div>
        {extended && (
          <span className="max-w-[320px] text-[12.5px] leading-[1.5] text-(--fg-muted)">
            {REASSURANCE}
          </span>
        )}
      </div>
    </div>
  );
}
